// Light/dark color mode for the hub shell — persisted through the existing
// hubMetadata.background field (the value the old "Change background" feature wrote).
// Light mode is a warm off-white; dark mode is a dark grey from the same neutral family as the
// design system's #3b3b3e text.
//
// Single source of truth for the two mode colors. Foreground (icons/text) is the
// inverse of the background; the rail's injected <style> reads these so there's only
// one place to retune the palette.

export const LIGHT_MODE_BG = '#F6F6F4';
// The light background light mode shipped with during its first, short-lived rollout, before the hub
// went dark-only. Still honoured when resolving the stored value so anyone who picked light back
// then stays light instead of being silently flipped to dark by the new hex.
const LEGACY_LIGHT_MODE_BG = '#F4F4F5';
export const DARK_MODE_BG = '#2B2B2E';
export const LIGHT_MODE_FG = '#3B3B3E';
export const DARK_MODE_FG = '#F5F3ED';
// The Venn wordmark at the foot of the rail. Dark mode keeps the muted warm grey it shipped with;
// light mode reuses LIGHT_MODE_FG so it doesn't wash out against the light shell.
export const WORDMARK_FG_DARK = '#AEABA8';
// Default dark-mode shell color used for every brand when the brand-color background flag is OFF
// (hub_brand_color_background). The gradient is built from this exactly as from a real brand
// primary-dark, so all orgs get the same navy until the flag opts them into their brand color.
export const DEFAULT_SHELL_BRAND_COLOR = '#2C4D6A';

export type ColorMode = 'light' | 'dark';

// How long the shell takes to cross-fade between the two modes, and the easing it uses — the same
// curve the rail already opens and closes with, so the two motions read as one system. Exported
// because the fade is spread across the layout (background layers, org logo) and the rail's injected
// <style>, and they must agree or the surfaces finish at visibly different times.
export const SHELL_TRANSITION_MS = 300;
export const SHELL_TRANSITION_EASE = 'cubic-bezier(0.2, 0, 0, 1)';

function normalizeHex(value: string): string {
  return value.replace('#', '').toLowerCase();
}

const LIGHT_MODE_BACKGROUNDS = [LIGHT_MODE_BG, LEGACY_LIGHT_MODE_BG].map(normalizeHex);

// Dark is the default. Only an explicit light selection (one of the light hexes, written by the
// profile-menu toggle) resolves to light; every other stored value — legacy background images, other
// colors, or nothing at all — resolves to dark. So existing users keep the dark shell they have
// today and only see light once they pick it themselves.
export function resolveColorMode(storedBackground: string | null | undefined): ColorMode {
  return LIGHT_MODE_BACKGROUNDS.includes(normalizeHex(storedBackground ?? '')) ? 'light' : 'dark';
}

export function colorModeBackground(mode: ColorMode): string {
  return mode === 'dark' ? DARK_MODE_BG : LIGHT_MODE_BG;
}

export function colorModeForeground(mode: ColorMode): string {
  return mode === 'dark' ? DARK_MODE_FG : LIGHT_MODE_FG;
}

export interface BrandColors {
  primary: string;
  primaryDark: string;
}

// The shell (header + sidebar) background. Dark mode draws the org's brand color as a soft vertical
// gradient (primary → primaryDark), falling back to the neutral dark when the org has no brand
// color. Light mode is one flat warm off-white — no gradient, no brand tint — so it reads as a
// single calm surface. Foreground stays the mode's inverse (see above).
const LIGHT_MODE_SHELL = LIGHT_MODE_BG;

// Darken a #rrggbb (or #rgb) hex toward black by `amount` (0–1).
function darken(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  if (full.length !== 6) return hex;
  const channel = (i: number) => Math.max(0, Math.round(parseInt(full.slice(i, i + 2), 16) * (1 - amount)));
  return `#${[channel(0), channel(2), channel(4)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

// Blend a #rrggbb (or #rgb) hex toward a target hex by `amount` (0–1).
function mix(hex: string, target: string, amount: number): string {
  const parse = (h: string) => {
    const s = h.replace('#', '');
    const full =
      s.length === 3
        ? s
            .split('')
            .map((c) => c + c)
            .join('')
        : s;
    return full.length === 6 ? [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) : null;
  };
  const a = parse(hex);
  const b = parse(target);
  if (!a || !b) return hex;
  const channel = (i: number) => Math.max(0, Math.min(255, Math.round(a[i] + (b[i] - a[i]) * amount)));
  return `#${[channel(0), channel(1), channel(2)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

// Neutral grey the bottom of the dark-mode gradient eases toward (a desaturated mid-grey, not white).
const SHELL_GREY = '#8E8E93';

// The default dark shell: a deep navy field lit by two soft blue-teal glows — one high on the left,
// one rising from the bottom — with a fine grain over it so the large gradients don't band. Colours
// are sampled from the design reference. Layers are listed top-first, as CSS paints them.
// The navy that fills most of that field — the base gradient's main stop. Exported for surfaces that
// want the shell's colour flat, without its glows (the Backstage agent pane).
export const SHELL_NAVY = '#142232';
const SHELL_GRAIN = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.07 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`;
const DARK_ATMOSPHERE_SHELL = [
  SHELL_GRAIN,
  'radial-gradient(38% 34% at 38% 100%, rgba(125, 164, 166, 0.9) 0%, rgba(101, 150, 158, 0.55) 35%, rgba(33, 65, 84, 0) 100%)',
  'radial-gradient(30% 48% at 22% 22%, rgba(50, 88, 110, 0.95) 0%, rgba(34, 62, 79, 0.55) 45%, rgba(17, 27, 42, 0) 100%)',
  'radial-gradient(40% 40% at 12% 78%, rgba(32, 61, 79, 0.8) 0%, rgba(17, 27, 42, 0) 100%)',
  `linear-gradient(160deg, #05070d 0%, #111b2a 28%, ${SHELL_NAVY} 55%, #081221 80%, #080f1d 100%)`,
].join(', ');

// A brand colour with this little chroma (max − min channel) is a grey/charcoal: tinting the
// gradient with it only produces the flat grey the atmosphere replaces.
const NEUTRAL_BRAND_CHROMA = 24;

function isNeutralColor(hex: string): boolean {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  if (full.length !== 6) return true;
  const channels = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return Math.max(...channels) - Math.min(...channels) < NEUTRAL_BRAND_CHROMA;
}

export function shellBackground(mode: ColorMode, brand?: BrandColors | null): string {
  if (mode === 'light') return LIGHT_MODE_SHELL;
  const mid = brand?.primaryDark || brand?.primary || DARK_MODE_BG;
  // No brand, the default shell colour, or a neutral brand → the blue atmosphere. A genuinely
  // coloured brand keeps its own gradient below, so hub_brand_color_background still means something.
  if (mid === DEFAULT_SHELL_BRAND_COLOR || mid === DARK_MODE_BG || isNeutralColor(mid)) return DARK_ATMOSPHERE_SHELL;
  const top = darken(mid, 0.3);
  const bottom = mix(mid, SHELL_GREY, 0.6);
  // Stays dark-dominant at the top (a slightly darker tone) holding the brand's dark primary, then —
  // starting high (~30%) — eases over the whole lower half toward a neutral grey (not white/beige),
  // so the grey reads earlier and the bottom is greyer than before.
  return `linear-gradient(180deg, ${top} 0%, ${mid} 30%, ${bottom} 100%)`;
}
